import React, { useEffect, useState } from "react";
import { Loader2, Plus, UserX } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { ROLE_LABELS, useAuth } from "@/context/AuthContext";
import { DataTable, Field, PageHeader, StatusBadge } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const ROLE_DESC = {
  super_admin: "Full access incl. users, settings, period reopen",
  admin: "Full operational & admin access",
  accountant: "Full accounting/GST access, exports, period lock — no user management",
  sales: "Bookings, quotations, customers, invoices & payments",
  booking: "Bookings, quotations & customers only",
  viewer: "Read-only access to all modules",
};

export default function Users() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "viewer", is_active: true });
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/users").then((r) => setUsers(r.data.users)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); }, []);

  const submit = async () => {
    setSaving(true);
    try {
      if (editing) await api.put(`/users/${editing.id}`, form);
      else await api.post("/users", form);
      toast.success("User saved");
      setOpen(false);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
    setSaving(false);
  };

  return (
    <div data-testid="users-page">
      <PageHeader title="Users & Roles" subtitle="Role-based access control for your team"
        testid="users-header"
        actions={<Button className="bg-[#0D4F4A] hover:bg-[#136A63]" data-testid="users-create-button"
          onClick={() => { setEditing(null); setForm({ name: "", email: "", password: "", role: "viewer", is_active: true }); setOpen(true); }}>
          <Plus className="h-4 w-4 mr-1" /> New User</Button>} />
      <DataTable testid="users-table"
        columns={[
          { key: "name", label: "Name", render: (r) => (
            <div><span className="font-medium">{r.name}</span>
              <div className="text-xs text-[#5C6B69]">{r.email}</div></div>) },
          { key: "role", label: "Role", render: (r) => (
            <div><span className="font-semibold text-[#0D4F4A]">{ROLE_LABELS[r.role]}</span>
              <div className="text-xs text-[#5C6B69] max-w-72">{ROLE_DESC[r.role]}</div></div>) },
          { key: "is_active", label: "Status", render: (r) => (
            <StatusBadge value={r.is_active === false ? "cancelled" : "paid"} testid={`user-status-${r.id}`} />) },
          { key: "_a", label: "", align: "right", render: (r) => (
            <div className="flex justify-end gap-1">
              <Button size="sm" variant="ghost" data-testid={`user-edit-${r.id}`}
                onClick={() => { setEditing(r); setForm({ name: r.name, email: r.email, password: "", role: r.role, is_active: r.is_active !== false }); setOpen(true); }}>Edit</Button>
              {r.email !== me?.email && r.is_active !== false && (
                <Button size="sm" variant="ghost" className="text-red-500" data-testid={`user-deactivate-${r.id}`}
                  onClick={async () => {
                    if (!window.confirm(`Deactivate ${r.name}?`)) return;
                    await api.delete(`/users/${r.id}`);
                    toast.success("User deactivated");
                    load();
                  }}><UserX className="h-3.5 w-3.5" /></Button>
              )}
            </div>) },
        ]}
        rows={users} empty="No users" />
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="user-form-dialog">
          <DialogHeader><DialogTitle className="font-display">{editing ? "Edit User" : "New User"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <Field label="Full Name" required>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="bg-white border-[#E5E0D8]" data-testid="user-name" /></Field>
            <Field label="Email" required>
              <Input type="email" value={form.email} disabled={!!editing}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="bg-white border-[#E5E0D8]" data-testid="user-email" /></Field>
            <Field label={editing ? "Reset Password (leave blank to keep)" : "Password"} required={!editing}>
              <Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })}
                className="bg-white border-[#E5E0D8]" data-testid="user-password" placeholder="Min 6 characters" /></Field>
            <Field label="Role" required>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger className="bg-white border-[#E5E0D8]" data-testid="user-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(ROLE_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
            <p className="text-xs text-[#5C6B69]">{ROLE_DESC[form.role]}</p>
            {editing && (
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={form.is_active} className="accent-[#0D4F4A]"
                  onChange={(e) => setForm({ ...form, is_active: e.target.checked })} data-testid="user-active" />
                Account active
              </label>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={submit} disabled={saving} className="bg-[#0D4F4A] hover:bg-[#136A63]" data-testid="user-submit">
              {saving && <Loader2 className="h-4 w-4 mr-1 animate-spin" />} Save User</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
